/**
 * WalkInPage — no-login "Open Chair" walk-in booking (Jose 2026-10-19).
 * -------------------------------------------------------------------------
 * QR at the shop → this page. Flow: Name + Phone → soonest open times (quarter-hour,
 * next-available across ANY barber) → pick a service → book. Identity is resolved
 * SERVER-SIDE by bookingService.createAppointment (resolveClientForBooking CF): an
 * existing client (matched by phone) links to their history; a first-timer becomes a
 * guest profile. The write stamps shopId and runs the same write-time double-book guard
 * as every other booking surface — so a walk-in lands on the barber's calendar exactly
 * like any appointment.
 *
 * Config (publicConfig/walkIn, mirrored from staff Settings): { enabled, incrementMinutes,
 * lookAheadMinutes }. Defaults: enabled true, 15-min grid, 120-min look-ahead.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import toast from 'react-hot-toast';
import {
  Scissors, Clock, Check, X, Zap, ChevronDown, ChevronRight,
  User, Phone, ArrowLeft, Loader2,
} from 'lucide-react';
import { bookingService } from '../services/bookingService';
import { openChairsService, OpenChairs, OpenChairSlot } from '../services/openChairsService';
import flatServiceDataService, { FlatServiceItem } from '../services/flatServiceDataService';
import { formatServiceName } from '../shared/serviceFormat';

/** First name only for the client-facing label (no barberName module on the public site). */
const firstNameOf = (n?: string) => ((n || '').trim().split(/\s+/)[0] || (n || ''));

interface WalkInConfig {
  enabled: boolean;
  incrementMinutes: number;   // round the next open chair to this grid (1/5/10/15) — :00/:05/:10 marks
  bufferMinutes: number;      // prep head-start: never offer a chair sooner than now + this
  minFitMinutes: number;      // shortest service that may squeeze a tight gap (15). Fades always need 30.
  lookAheadMinutes: number;   // only look this far ahead for the soonest chair
  slotCount: number;          // how many backup times to reveal under "More times available"
}
// Jose 2026-10-27: soonest-possible walk-in. 5-min grid, no buffer, 15-min squeeze allowed (fades excepted).
const DEFAULT_CONFIG: WalkInConfig = { enabled: true, incrementMinutes: 5, bufferMinutes: 0, minFitMinutes: 15, lookAheadMinutes: 120, slotCount: 4 };
// A "fade" (any low/mid/high fade) can never be squeezed into a sub-30 opening — it needs the full cut time.
const FADE_MIN_DURATION = 30;
const isFadeService = (name?: string) => /\bfade/i.test(name || '');
// Effective fit a service needs in a walk-in gap: its own duration, but a fade is floored at 30.
const walkInFitFor = (svc: { name?: string; duration?: number }) =>
  Math.max(svc.duration || 30, isFadeService(svc.name) ? FADE_MIN_DURATION : 0);

type Step = 'identity' | 'time' | 'service' | 'confirm' | 'done';

const digitsOf = (p: string) => (p || '').replace(/\D/g, '');

const WalkInPage: React.FC = () => {
  const [config, setConfig] = useState<WalkInConfig>(DEFAULT_CONFIG);
  const [configLoaded, setConfigLoaded] = useState(false);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  const [step, setStep] = useState<Step>('identity');
  const [chairs, setChairs] = useState<OpenChairs | null>(null);
  const [services, setServices] = useState<FlatServiceItem[]>([]);
  const [loadingChairs, setLoadingChairs] = useState(false);

  const [slot, setSlot] = useState<OpenChairSlot | null>(null);
  const [barber, setBarber] = useState<{ id: string; name: string } | null>(null);
  const [service, setService] = useState<FlatServiceItem | null>(null);
  // Names of services that fit the chosen opening (null = still probing). Fades drop out of tight gaps.
  const [fitServices, setFitServices] = useState<Set<string> | null>(null);
  const [showMoreTimes, setShowMoreTimes] = useState(false); // "More times available" expander
  const [expandedCat, setExpandedCat] = useState<Set<string>>(new Set());
  const [booking, setBooking] = useState(false);
  const [confirmed, setConfirmed] = useState<{ service: string; barber: string; time: string } | null>(null);

  // --- config (publicConfig/walkIn) ---
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'publicConfig', 'walkIn'));
        if (snap.exists()) {
          const d = snap.data() as any;
          setConfig({
            enabled: d.enabled !== false,
            incrementMinutes: Number(d.incrementMinutes) > 0 ? Number(d.incrementMinutes) : DEFAULT_CONFIG.incrementMinutes,
            bufferMinutes: Number(d.bufferMinutes) >= 0 ? Number(d.bufferMinutes) : DEFAULT_CONFIG.bufferMinutes,
            minFitMinutes: Number(d.minFitMinutes) > 0 ? Number(d.minFitMinutes) : DEFAULT_CONFIG.minFitMinutes,
            lookAheadMinutes: Number(d.lookAheadMinutes) > 0 ? Number(d.lookAheadMinutes) : DEFAULT_CONFIG.lookAheadMinutes,
            slotCount: Number(d.slotCount) > 0 ? Number(d.slotCount) : DEFAULT_CONFIG.slotCount,
          });
        }
      } catch { /* missing doc → defaults */ }
      finally { setConfigLoaded(true); }
    })();
  }, []);

  const grouped = useMemo(() => {
    const g: Record<string, FlatServiceItem[]> = {};
    services.forEach((s) => { const c = s.categoryName || 'Other'; (g[c] ||= []).push(s); });
    return g;
  }, [services]);
  const toggleCat = (c: string) => setExpandedCat((prev) => {
    const n = new Set(prev); n.has(c) ? n.delete(c) : n.add(c); return n;
  });

  // Minutes from now until a slot (minutes-from-midnight). Used for "in N minutes" on the hero.
  const minutesUntil = (slotMinutes: number) => {
    const now = new Date();
    return slotMinutes - (now.getHours() * 60 + now.getMinutes());
  };

  const loadChairs = async () => {
    setShowMoreTimes(false);
    setFitServices(null);
    setLoadingChairs(true);
    try {
      const [c, svcs] = await Promise.all([
        openChairsService.getOpenChairs({
          searchDays: 1, // TODAY ONLY (Jose 2026-11-04): Open Chair = the next available on the SAME day, never
                         // roll forward to future days. If today's full, show the "see front desk / book later" state.
          maxSlots: config.slotCount,
          intervalMin: config.incrementMinutes,
          lookAheadMinutes: config.lookAheadMinutes,
          bufferMinutes: config.bufferMinutes,
          minFitMinutes: config.minFitMinutes,
        }),
        flatServiceDataService.getFlatServiceList(true),
      ]);
      setChairs(c);
      setServices(svcs);
    } catch (e) {
      console.error('Walk-in load failed:', e);
      toast.error('Could not load open times — please see the front desk.');
    } finally {
      setLoadingChairs(false);
    }
  };

  const continueFromIdentity = async () => {
    if (!name.trim()) { toast.error('Please enter your name.'); return; }
    if (digitsOf(phone).length < 10) { toast.error('Please enter a 10-digit mobile number.'); return; }
    setStep('time');
    await loadChairs();
  };

  const pickTime = async (s: OpenChairSlot) => {
    const b = s.barbers[0] || null; // auto-match: first barber free at this time
    setSlot(s);
    setBarber(b);
    setService(null);
    setExpandedCat(new Set());
    setStep('service');
    // Which services actually FIT this exact opening for the matched barber? A fade is floored at 30 min,
    // so a 15-min squeeze never offers one. Probe once per distinct effective fit-duration (15/30/60…).
    setFitServices(null); // null → "still checking"
    if (b && chairs) {
      try {
        const durs = Array.from(new Set(services.map(walkInFitFor)));
        const okByDur: Record<number, boolean> = {};
        await Promise.all(durs.map(async (d) => {
          const times = await bookingService
            .getAvailableSlots(b.id, chairs.date, config.incrementMinutes, d)
            .catch(() => [] as string[]);
          okByDur[d] = times.includes(s.time);
        }));
        const fit = new Set<string>();
        services.forEach((svc) => { if (okByDur[walkInFitFor(svc)]) fit.add(svc.name); });
        setFitServices(fit);
      } catch {
        setFitServices(new Set(services.map((svc) => svc.name))); // probe failed → don't block booking
      }
    }
  };

  const chooseService = (svc: FlatServiceItem) => {
    setService(svc);
    setExpandedCat(new Set());
    setStep('confirm');
  };

  const confirm = async () => {
    if (!chairs || !slot || !service) return;
    setBooking(true);
    // Try the matched barber; if that exact slot was just taken, fall through to the next barber
    // free at the same time (createAppointment throws on overlap via the write-time guard).
    const candidates = barber
      ? [barber, ...slot.barbers.filter((b) => b.id !== barber.id)]
      : slot.barbers;
    try {
      let lastErr: any = null;
      for (const b of candidates) {
        try {
          await bookingService.createAppointment({
            clientId: '',                 // resolved SERVER-SIDE (resolveClientForBooking) by phone/name
            clientName: name.trim(),
            clientPhone: phone.trim(),
            clientEmail: '',
            barberId: b.id,
            barberName: b.name,
            service: service.name,
            date: chairs.date,
            time: slot.time,
            duration: service.duration,
            price: service.price,
            status: 'scheduled',
            source: 'walk-in',
            reminderSent: false,
          });
          setConfirmed({ service: formatServiceName(service.name), barber: firstNameOf(b.name), time: slot.time });
          setStep('done');
          return;
        } catch (err: any) {
          if (err?.code === 'NO_SHOW_BLOCK') { toast.error(err.message || 'Please see the front desk to book.'); setBooking(false); return; }
          lastErr = err; // likely a just-taken overlap — try the next free barber
        }
      }
      console.error('Walk-in booking failed:', lastErr);
      toast.error('That time just filled up — grabbing fresh times.');
      setStep('time');
      await loadChairs();
    } finally {
      setBooking(false);
    }
  };

  const resetForNext = () => {
    setName(''); setPhone('');
    setSlot(null); setBarber(null); setService(null);
    setConfirmed(null); setChairs(null);
    setExpandedCat(new Set());
    setFitServices(null); setShowMoreTimes(false);
    setStep('identity');
  };

  // ---------- render ----------
  if (!configLoaded) {
    return <Shell><div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-slik-purple animate-spin" /></div></Shell>;
  }

  if (!config.enabled) {
    return (
      <Shell>
        <div className="text-center py-12">
          <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-4">
            <Scissors className="w-7 h-7 text-white/70" />
          </div>
          <h2 className="text-2xl font-extrabold text-white mb-2">Walk-ins are paused right now</h2>
          <p className="text-white/70 text-lg">Please see the front desk, or <Link to="/book" className="text-slik-magenta font-bold underline">book online</Link>.</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      {/* STEP: identity */}
      {step === 'identity' && (
        <div>
          <Hero title="Grab an open chair" subtitle="Walk right in — we’ll match you with the next open chair." />
          <div className="space-y-4 mt-6">
            <Field icon={<User className="w-5 h-5" />} label="Your name">
              <input
                value={name} onChange={(e) => setName(e.target.value)}
                placeholder="First & last name" autoFocus
                className="w-full bg-black/20 border-2 border-white/15 focus:border-slik-magenta rounded-xl px-4 py-4 text-white text-lg outline-none placeholder-white/40"
              />
            </Field>
            <Field icon={<Phone className="w-5 h-5" />} label="Mobile number">
              <input
                value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder="(973) 555-0100" inputMode="tel" type="tel"
                className="w-full bg-black/20 border-2 border-white/15 focus:border-slik-magenta rounded-xl px-4 py-4 text-white text-lg outline-none placeholder-white/40"
              />
            </Field>
            <p className="text-white/50 text-sm px-1">We’ll text your confirmation. If you’ve been here before, we’ll find your history automatically.</p>
            <button
              onClick={continueFromIdentity}
              className="w-full bg-gradient-to-r from-slik-purple to-slik-magenta text-white py-4 rounded-xl font-extrabold text-lg shadow-lg active:scale-[0.99] transition-transform"
            >
              See open times →
            </button>
          </div>
        </div>
      )}

      {/* STEP: time — ONE option: the next open chair. "More times" only if the barber says a few minutes. */}
      {step === 'time' && (
        <div>
          <BackBar onBack={() => setStep('identity')} label="Next open chair" />
          {loadingChairs ? (
            <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-slik-purple animate-spin" /></div>
          ) : !chairs || chairs.slots.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-10 h-10 text-white/40 mx-auto mb-3" />
              <p className="text-white text-xl font-bold mb-1">No open chairs right now</p>
              <p className="text-white/60 text-lg">Please see the front desk, or <Link to="/book" className="text-slik-magenta font-bold underline">book a later time</Link>.</p>
            </div>
          ) : (() => {
            const hero = chairs.slots[0];
            const heroBarber = hero.barbers[0];
            const mins = chairs.dayLabel === 'today' ? minutesUntil(hero.minutes) : null;
            const whenLabel = mins == null ? chairs.dayLabel : mins <= 1 ? 'in about a minute' : `in ${mins} minutes`;
            const more = chairs.slots.slice(1);
            return (
              <>
                {/* HERO — the next open chair, written exactly: "Next Open Chair · 9:05 · in 2 minutes · with Diana" */}
                <button
                  onClick={() => pickTime(hero)}
                  className="w-full text-left bg-gradient-to-br from-slik-purple/30 to-slik-magenta/20 border-2 border-slik-magenta rounded-3xl p-6 active:scale-[0.99] transition-transform"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-slik-green/70" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-slik-green" />
                    </span>
                    <span className="text-slik-green font-bold uppercase tracking-wide text-sm">Next open chair</span>
                  </div>
                  <p className="text-white font-extrabold text-5xl leading-none">{hero.time}</p>
                  <p className="text-white/85 text-xl mt-3 font-semibold">
                    {whenLabel}{heroBarber ? <> · with {firstNameOf(heroBarber.name)}</> : null}
                  </p>
                  <span className="inline-block mt-5 bg-white text-slik-navy font-extrabold text-lg px-6 py-3 rounded-full">Grab this chair →</span>
                </button>

                {/* More times — collapsed. Opened only if the barber offers a few minutes out. */}
                {more.length > 0 && (
                  <div className="mt-5">
                    {!showMoreTimes ? (
                      <button onClick={() => setShowMoreTimes(true)} className="w-full text-slik-magenta font-bold py-3 underline">
                        More times available
                      </button>
                    ) : (
                      <>
                        <p className="text-white/60 text-sm mb-2 px-1">If your barber says a few minutes, pick one:</p>
                        <div className="grid grid-cols-3 gap-3">
                          {more.map((s) => (
                            <button
                              key={s.time}
                              onClick={() => pickTime(s)}
                              className="bg-white/5 border-2 border-slik-purple/50 hover:border-slik-magenta text-white font-bold text-lg px-2 py-4 rounded-2xl active:scale-[0.98] transition-all"
                            >
                              {s.time.replace(/:00 /, ' ')}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* STEP: service */}
      {step === 'service' && slot && (
        <div>
          <BackBar onBack={() => setStep('time')} label="Pick a service" />
          <div className="flex items-center gap-2 mb-2 text-white/80 text-lg">
            <Clock className="w-5 h-5 text-slik-magenta" />
            <span className="font-bold text-white">{slot.time}</span>
            {barber && <span className="text-white/60">· with {firstNameOf(barber.name)}</span>}
          </div>
          {fitServices === null
            ? <p className="text-white/45 text-sm mb-4">Checking what fits this opening…</p>
            : <p className="text-white/45 text-sm mb-4">Showing what fits this opening. Fades need a full chair — grayed services need more time.</p>}
          <div className="space-y-3">
            {Object.entries(grouped).map(([cat, items]) => {
              const isOpen = expandedCat.has(cat);
              return (
                <div key={cat} className="border-2 border-white/12 rounded-2xl overflow-hidden">
                  <button onClick={() => toggleCat(cat)} className="w-full flex items-center justify-between p-4 bg-white/5">
                    <div className="flex items-center gap-2">
                      {isOpen ? <ChevronDown className="h-6 w-6 text-slik-magenta" /> : <ChevronRight className="h-6 w-6 text-slik-magenta" />}
                      <span className="font-bold text-white text-lg">{cat}</span>
                      <span className="text-sm text-white/50">({items.length})</span>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="divide-y divide-white/8">
                      {items.map((svc) => {
                        const fits = fitServices === null || fitServices.has(svc.name);
                        return (
                          <button
                            key={svc.id}
                            disabled={!fits}
                            onClick={() => fits && chooseService(svc)}
                            className={`w-full p-4 text-left flex items-center gap-3 ${fits ? 'active:bg-white/5' : 'opacity-40 cursor-not-allowed'}`}
                          >
                            <div className={`w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 ${!svc.imageUrl ? 'bg-slik-purple/20 flex items-center justify-center' : ''}`}>
                              {svc.imageUrl ? <img src={svc.imageUrl} alt={svc.name} className="w-full h-full object-cover" /> : <Scissors className="w-6 h-6 text-slik-magenta" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-bold text-white text-lg truncate">{formatServiceName(svc.name)}</p>
                              <p className="text-sm text-white/55">{fits ? `${svc.duration} min` : 'Needs a longer opening'}</p>
                            </div>
                            {fits
                              ? <span className="font-extrabold text-slik-green text-lg">${Number(svc.price).toFixed(0)}</span>
                              : <span className="text-xs text-white/40 flex-shrink-0">more time</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* STEP: confirm */}
      {step === 'confirm' && slot && service && barber && (
        <div>
          <BackBar onBack={() => setStep('service')} label="Confirm" />
          <div className="bg-slik-green/10 rounded-2xl p-5 border-2 border-slik-green/30 mb-5">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-6 h-6 text-slik-green" />
              <p className="font-extrabold text-slik-green text-xl">Ready to book</p>
            </div>
            <div className="space-y-3 text-lg">
              <Row k="Name" v={name.trim()} />
              <Row k="Service" v={formatServiceName(service.name)} />
              <Row k="Barber" v={firstNameOf(barber.name)} />
              <Row k="When" v={`${chairs?.dayLabel} · ${slot.time}`} />
              <div className="flex justify-between pt-3 border-t border-slik-green/25">
                <span className="text-white/70">Total</span>
                <span className="font-extrabold text-2xl text-slik-green">${Number(service.price).toFixed(0)}</span>
              </div>
            </div>
          </div>
          <button
            onClick={confirm}
            disabled={booking}
            className="w-full bg-gradient-to-r from-slik-purple to-slik-magenta text-white py-5 rounded-2xl font-extrabold text-xl shadow-lg active:scale-[0.99] transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {booking ? <><Loader2 className="w-6 h-6 animate-spin" /> Booking…</> : 'Confirm walk-in'}
          </button>
        </div>
      )}

      {/* STEP: done */}
      {step === 'done' && confirmed && (
        <div className="text-center py-8">
          <div className="w-20 h-20 rounded-full bg-slik-green/20 border-2 border-slik-green flex items-center justify-center mx-auto mb-5">
            <Check className="w-10 h-10 text-slik-green" />
          </div>
          <h2 className="text-3xl font-extrabold text-white mb-2">You’re booked!</h2>
          <p className="text-white/80 text-xl mb-1">{confirmed.service} with {confirmed.barber}</p>
          <p className="text-white/60 text-lg mb-8">{chairs?.dayLabel} at {confirmed.time}</p>
          <p className="text-white/55 text-base mb-8">Have a seat and relax — we’ll text your confirmation now. Stepped out? No problem — we’ll alert you the moment your chair’s ready.</p>
          <button onClick={resetForNext} className="text-slik-magenta font-bold text-lg underline">Book another walk-in</button>
        </div>
      )}
    </Shell>
  );
};

// ---------- presentational helpers ----------
const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-[100dvh] bg-slik-navy text-white px-5 py-8">
    <div className="max-w-md mx-auto">
      <div className="flex items-center justify-center mb-6">
        <span className="text-2xl font-extrabold tracking-wide">SLIK <span className="text-slik-magenta">Walk-In</span></span>
      </div>
      <div className="bg-white/[0.04] border-2 border-white/10 rounded-3xl p-5 shadow-2xl">
        {children}
      </div>
    </div>
  </div>
);

const Hero: React.FC<{ title: string; subtitle: string }> = ({ title, subtitle }) => (
  <div className="text-center">
    <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-slik-purple to-slik-magenta flex items-center justify-center mx-auto mb-3">
      <Scissors className="w-7 h-7 text-white" />
    </div>
    <h1 className="text-3xl font-extrabold text-white">{title}</h1>
    <p className="text-white/65 text-lg mt-1">{subtitle}</p>
  </div>
);

const Field: React.FC<{ icon: React.ReactNode; label: string; children: React.ReactNode }> = ({ icon, label, children }) => (
  <div>
    <label className="flex items-center gap-2 text-white/70 font-semibold mb-2">
      <span className="text-slik-magenta">{icon}</span>{label}
    </label>
    {children}
  </div>
);

const BackBar: React.FC<{ onBack: () => void; label: string }> = ({ onBack, label }) => (
  <div className="flex items-center gap-3 mb-5">
    <button onClick={onBack} className="p-2 -ml-2 rounded-lg active:bg-white/10"><ArrowLeft className="w-6 h-6 text-white/80" /></button>
    <h2 className="text-2xl font-extrabold text-white">{label}</h2>
  </div>
);

const Row: React.FC<{ k: string; v: string }> = ({ k, v }) => (
  <div className="flex justify-between">
    <span className="text-white/60">{k}</span>
    <span className="font-bold text-white">{v}</span>
  </div>
);

export default WalkInPage;
